/**
 * ODBM ATC Price — makes the optional price inside the Add-to-cart button
 * ("Add to cart — $25.00") reflect quantity: unit price × the quantity
 * input's value, live.
 *
 * Whole-file ODBM module (basename `odbm-*`), loaded from the buy_buttons
 * ODB CUSTOM block in snippets/odbm-main-product-blocks.liquid when the
 * show_atc_price setting is on. Tested in tests/odbm-atc-price.test.js.
 *
 * Markup contract (rendered by the buy_buttons ODB CUSTOM span):
 *   <span id="OdbmAtcPrice-{sectionId}">
 *     <span data-odbm-atc-amount data-odbm-unit-price="2500"
 *           data-odbm-price-breaks="[[10,2250],[25,2000]]"> — $25.00</span>
 *   </span>
 * The unit price lives on the INNER span because product-info.js refreshes
 * the outer span via innerHTML swap on variant change — inner attributes
 * ride along, outer ones would go stale.
 *
 * B2B quantity price breaks: `data-odbm-price-breaks` is
 * [[minimum_quantity, unit_price_in_cents]…], ascending, emitted by Liquid for
 * the selected variant only when the buyer's catalog configures them. The
 * qualifying tier is picked off cart quantity + entered quantity — the basis
 * Shopify prices the line on, and the one the vendor <price-per-item> uses.
 *
 * Events:
 *   - delegated input/change on input[name="quantity"] (fired by the vendor
 *     <quantity-input> +/- buttons and by typing) — the section id comes
 *     from the input's form="product-form-{sectionId}" attribute;
 *   - document "variant:changed" (dispatched by product-info.js AFTER its
 *     innerHTML swaps) — re-applies the current quantity to the freshly
 *     rendered unit price;
 *   - the vendor FoxThemeEvents cartUpdate pub-sub (when global.js is loaded)
 *     — refreshes each quantity input's data-cart-quantity from the payload so
 *     the break tier stays right after an in-page add to cart.
 *
 * Uses the vendor's global formatMoney() (global.js) with
 * FoxThemeSettings.money_format, which already respects the shop's
 * currency-code setting. $0.00 variants render no amount element at all
 * (Liquid), so this module is a no-op for them.
 */
(() => {
  if (window.OdbmAtcPriceInitialized) return;
  window.OdbmAtcPriceInitialized = true;

  const FORM_PREFIX = "product-form-";

  function inputFor(sectionId) {
    return document.querySelector(
      `input[name="quantity"][form="${FORM_PREFIX}${sectionId}"]`
    );
  }

  function quantityFor(input) {
    const qty = input ? Number.parseInt(input.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  // Shopify qualifies a break on the whole cart line, not just the units being
  // added. data-cart-quantity is kept fresh on variant change by the vendor
  // QuantityInput.updateQuantityRules swap.
  function cartQuantityFor(input) {
    const inCart = input ? Number.parseInt(input.dataset.cartQuantity, 10) : 0;
    return Number.isFinite(inCart) && inCart > 0 ? inCart : 0;
  }

  function priceBreaksFor(amountEl) {
    const raw = amountEl.dataset.odbmPriceBreaks;
    if (!raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (pair) =>
          Array.isArray(pair) &&
          Number.isInteger(pair[0]) &&
          Number.isInteger(pair[1]) &&
          pair[0] > 0 &&
          pair[1] > 0
      )
      .sort((a, b) => a[0] - b[0]);
  }

  function unitPriceFor(basePrice, breaks, qualifyingQty) {
    let unitPrice = basePrice;
    for (const [minimumQuantity, breakPrice] of breaks) {
      if (qualifyingQty >= minimumQuantity) unitPrice = breakPrice;
    }
    return unitPrice;
  }

  function refresh(sectionId) {
    const wrapper = document.getElementById(`OdbmAtcPrice-${sectionId}`);
    if (!wrapper) return;
    const amountEl = wrapper.querySelector("[data-odbm-atc-amount]");
    if (!amountEl) return;
    const basePrice = Number.parseInt(amountEl.dataset.odbmUnitPrice, 10);
    if (!Number.isFinite(basePrice) || basePrice <= 0) return;
    if (typeof formatMoney !== "function") return;

    const input = inputFor(sectionId);
    const quantity = quantityFor(input);
    const unitPrice = unitPriceFor(
      basePrice,
      priceBreaksFor(amountEl),
      cartQuantityFor(input) + quantity
    );

    const total = unitPrice * quantity;
    const formatted = formatMoney(
      total,
      window.FoxThemeSettings && window.FoxThemeSettings.money_format
    );
    amountEl.textContent = ` — ${formatted}`;
  }

  function refreshAll() {
    document.querySelectorAll('[id^="OdbmAtcPrice-"]').forEach((wrapper) => {
      refresh(wrapper.id.replace("OdbmAtcPrice-", ""));
    });
  }

  function onQuantityEvent(event) {
    const input = event.target;
    if (!input || input.name !== "quantity") return;
    const form = input.getAttribute("form") || "";
    if (!form.startsWith(FORM_PREFIX)) return;
    refresh(form.slice(FORM_PREFIX.length));
  }

  // An add to cart can move the line into the next tier, so refresh from the
  // payload; data-cart-quantity is the attribute the vendor <price-per-item>
  // reads too. PUB_SUB_EVENTS is a global const in global.js, not a window
  // property — hence the typeof guard below.
  function syncCartQuantities(response) {
    const cart = response?.cart;
    if (!cart) return;
    document
      .querySelectorAll('input[name="quantity"][data-quantity-variant-id]')
      .forEach((input) => {
        const variantId = input.dataset.quantityVariantId;
        let inCart;
        if (Array.isArray(cart.items)) {
          const line = cart.items.find(
            (item) => String(item.variant_id) === variantId
          );
          inCart = line ? line.quantity : 0;
        } else if (String(cart.variant_id) === variantId) {
          inCart = cart.quantity;
        }
        if (Number.isFinite(inCart)) input.dataset.cartQuantity = String(inCart);
      });
    refreshAll();
  }

  document.addEventListener("change", onQuantityEvent);
  document.addEventListener("input", onQuantityEvent);
  if (
    typeof PUB_SUB_EVENTS !== "undefined" &&
    typeof window.FoxThemeEvents?.subscribe === "function"
  ) {
    window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, syncCartQuantities);
  }
  // product-info.js dispatches this AFTER swapping #OdbmAtcPrice-* content,
  // so re-applying the quantity here always sees the new variant's price.
  document.addEventListener("variant:changed", refreshAll);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshAll);
  } else {
    refreshAll();
  }
})();
